import { CardBtnArrowIcon, TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import throttle from 'lodash/throttle';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

interface ScrollTeaserProps {
  readonly offset?: number;
  readonly size?: 'small' | 'default';
  readonly outline?: boolean;
  readonly content?: MessageKey;
}

const ScrollTeaser = ({
  offset = 50,
  size = 'default',
  outline,
  content = 'meet_oura_ring_scroll_to_discover',
}: ScrollTeaserProps) => {
  const [visible, setVisible] = useState(true);
  const t = useTranslations();

  useEffect(() => {
    const handleScroll = () => {
      const isVisible = offset > window.scrollY;
      setVisible(isVisible);
    };

    const throttleScroll = throttle(handleScroll, 50);
    window.addEventListener('scroll', throttleScroll);

    return () => window.removeEventListener('scroll', throttleScroll);
  }, [offset]);

  return (
    <div
      aria-hidden="true"
      className={cx(
        'hidden text-sandstone-500 transition-opacity duration-250',
        'md:fixed md:top-2/3 md:left-0 md:block md:w-2/12 md:text-center',
        {
          'md:opacity-0': !visible,
          'md:opacity-100': visible,
        },
      )}
    >
      <TypographyV3
        align="center"
        variant="body-small"
        color="inherit"
        className="px-4 pb-6"
      >
        {t(content)}
      </TypographyV3>

      <div
        data-cy="scroll-teaser-arrow"
        className={cx('inline-flex items-center justify-center rounded-full', {
          'size-16': size === 'small',
          'size-20 bg-sandstone-500 text-white lg:size-24':
            !outline && size === 'default',
          'size-20 border border-gray-400 text-gray-400 lg:size-24':
            outline && size === 'default',
        })}
      >
        <CardBtnArrowIcon className="rotate-90" />
      </div>
    </div>
  );
};

export default ScrollTeaser;
